/* eslint max-statements: 0 */
import uuid from 'node-uuid'
import moment from 'moment'
import { createSelector } from 'reselect'
import { getFormValues } from 'redux-form'
import { selectUnitPreference, selectTimeZone, selectUserId } from 'services/user/selectors'
import { selectActivityTypes } from 'services/activityTypes/selectors'
import { selectSiteSlug } from 'services/site/selectors'
import * as helpers from './helpers'
import { convert } from 'shared/units'
import {
  METERS,
  IMPERIAL,
  MILES,
  KILOMETERS,
  JOULES,
  KILOCALORIES,
  METERS_PER_SECOND,
  KILOMETERS_PER_HOUR,
  MILES_PER_HOUR,
  SECOND_MI,
  SECOND_KM,
} from 'shared/units/constants'

export const selectError = (state) => state.workouts.error
export const selectWorkoutValues = (state) => getFormValues('workoutForm')(state)
export const selectUserWorkouts = (state) => state.workouts.userWorkouts
export const selectNoUserWorkoutsFound = (state) => state.workouts.noUserWorkoutsFound

export const selectUserWorkoutsWithActivity = createSelector(
  selectUserWorkouts,
  selectActivityTypes,
  (workouts, activityTypes) => {
    if (!workouts || !activityTypes) { return }
    return workouts.map((workout) => {
      const activityTypeId = workout._links.activity_type[0].id
      const activityObj = activityTypes.find((activity) => activityTypeId === activity._links.root[0].id)
      workout.activity = activityObj ? activityObj : {}
      return workout
    })
  },
)

export const selectChosenActivity = createSelector(
  selectWorkoutValues,
  selectActivityTypes,
  (values, activityTypes) => {
    if (!values || !values.activityType || !activityTypes || !activityTypes.length) { return null }
    return activityTypes.find(type => type._links.self[0].id === values.activityType)
  },
)

export const selectShowRouteInputs = createSelector(
  selectChosenActivity,
  (activity) => activity && helpers.isRoutableActivity(activity),
)

export const selectShowDistanceInputs = createSelector(
  selectChosenActivity,
  (activity) => activity && helpers.isDistanceActivity(activity),
)

export const selectShowSpeedInput = createSelector(
  selectChosenActivity,
  (activity) => activity && helpers.isSpeedActivity(activity),
)

export const selectShowPaceInput = createSelector(
  selectChosenActivity,
  (activity) => activity && helpers.isPaceActivity(activity),
)

export const selectShowStepsInput = createSelector(
  selectChosenActivity,
  (activity) => activity && helpers.isStepsActivity(activity),
)

export const selectShowSwimInputs = createSelector(
  selectChosenActivity,
  (activity) => activity && helpers.isSwimActivity(activity),
)

export const selectWorkoutForm = (name) => (state) => state.form[name] || null

export const selectWorkoutSaving = (state) => state.workouts.workoutSaving

export const selectWorkoutCopy = (state) => state.workouts.workoutCopy

/**
 * Given a copy of a workout, returns an object suitable for initial values in
 * a redux form.
 *
 * Returns an object like:
 *
 * {
 *   id: "1671765943",
 *   privacy: "/v7.1/privacy_option/1/",
 *   name: "foo",
 *   gear: "/v7.1/usergear/2727267/",
 *   startDate: "2016-12-20T07:00:00.000Z",
 *   startTime: "2016-12-20T07:00:00.000Z",
 *   notes: "asdf",
 *   activityType: "16",
 *   duration_hh: "1",
 *   duration_mm: "0",
 *   duration_ss: "0",
 *   distance: "1.00",
 *   energy: "157.00",
 *   heartrate: "160",
 *   steps: "8723",
 *   speed: "7.50",
 *   pace: "8.00",
 *   site: "run"
 * }
 *
 * @param  {Function} selectWorkoutCopy
 * @param  {Function} selectUnitPreference
 * @param  {Function} selectSiteSlug
 * @return {Object}
 */
export const selectInitialFormData = createSelector(
  selectWorkoutCopy,
  selectUnitPreference,
  selectSiteSlug,
  (copy, system, siteSlug) => {

    if (!copy) { return }

    const { aggregates, _links: links } = copy

    let gear, activityType, duration, distance, energy, speed, pace, privacy

    if (links.usergear) { gear = links.usergear[0].href }
    if (links.privacy) { privacy = links.privacy[0].href }
    if (links.activity_type) { activityType = links.activity_type[0].id }

    if (copy.aggregates
      && typeof copy.aggregates.active_time_total === 'number'
      && copy.aggregates.active_time_total > 0) {
      duration = copy.aggregates.active_time_total
    }

    if (aggregates && typeof aggregates.distance_total === 'number' && aggregates.distance_total > 0) {
      distance = convert(aggregates.distance_total).from(METERS)
        .to(system === IMPERIAL ? MILES : KILOMETERS)
        .toFixed(2)
    }

    if (aggregates && typeof aggregates.metabolic_energy_total === 'number' && aggregates.metabolic_energy_total > 0) {
      energy = convert(aggregates.metabolic_energy_total)
        .from(JOULES)
        .to(KILOCALORIES)
        .toFixed(2)
    }

    if (aggregates && aggregates.speed_avg) {
      speed = convert(aggregates.speed_avg)
        .from(METERS_PER_SECOND)
        .to(system === IMPERIAL ? MILES_PER_HOUR : KILOMETERS_PER_HOUR)
        .toFixed(2)

      const _pace = convert(aggregates.speed_avg)
        .from(METERS_PER_SECOND)
        .to(system === IMPERIAL ? SECOND_MI : SECOND_KM)

      pace = Math.round(_pace)
    }

    const heartrate = aggregates.heartrate_avg ? aggregates.heartrate_avg.toString() : undefined
    const steps = aggregates.steps_total ? aggregates.steps_total.toString() : undefined

    const result = {
      id: links.self[0].id,
      privacy: privacy,
      name: copy.name,
      gear: gear,
      startDate: moment(copy.start_datetime),
      startTime: moment(copy.start_datetime),
      notes: copy.notes,
      activityType: activityType,
      route: copy.route,
      duration: duration,
      distance: distance,
      energy: energy,
      heartrate: heartrate,
      steps: steps,
      speed: speed,
      pace: pace,
      site: siteSlug,
    }

    return result
  }
)

export const selectWorkoutCopyLoading = (state) => state.workouts.workoutCopyLoading

/**
 * Serializes the state of the redux-form, for use in POST/PUT calls.
 * Given a redux form w/ values, it turns it into this kind of shape:
 *
 * {
 *   "name": "My Morning run",
 *   "notes": "Was very cold",
 *   "start_datetime": "2016-12-20T01:00:00-06:00",
 *   "start_locale_timezone": "America/Chicago",
 *   "activity_type": "/v7.1/activity_type/16/",
 *   "usergear": "/v7.1/usergear/2727267/",
 *   "source": "fitness_web",
 *   "reference_key": "fitness_web_2016-12-20T14:27:50.352Z"
 *   "aggregates": {
 *     "distance_total": 1609.34,
 *     "metabolic_energy_total": 655214.4,
 *     "elapsed_time_total": 3600,
 *     "heartrate_avg": "160",
 *     "speed_avg": 3.3528
 *   },
 *   "sharing": {
 *     "facebook": true
 *   },
 * }
 *
 * @param  {String formName                - redux form name
 * @param  {Function} selectUnitPreference - selector
 * @param  {Function} selectActivityTypes  - selector
 * @param  {Function} selectTimeZone       - selector
 * @return {Object}
 */
export const selectApiDataFromForm = (formName) => createSelector(
  selectWorkoutCopy,
  selectWorkoutForm(formName),
  selectUnitPreference,
  selectActivityTypes,
  selectTimeZone,
  selectUserId,
  (copy, form, system, activityTypes, startTimeZone, userId) => {
    if (!form || !form.values) { return null }

    const {
      name,
      startDate,
      pace,
      speed,
      distance,
      duration,
      startTime,
      route,
      gear,
      energy,
      activityType: activityTypeId,
      shareOnFacebook,
      shareOnTwitter,
      heartrate: heartRate,
      notes,
      steps,
      mode,
      site,
    } = form.values
    const isEditing = mode === 'edit'
    const isCreating = mode === 'create'

    const activityType = activityTypes.find(type => {
      return type._links.self[0].id === activityTypeId
    })

    // always start with the original when saving an 'edit'
    const existingWorkoutData = isEditing ? { ...copy } : {}

    const hasTimeSeries = existingWorkoutData.has_time_series

    if (existingWorkoutData && existingWorkoutData._links) {
      if (existingWorkoutData._links.privacy) {
        const privacyLink = existingWorkoutData._links.privacy[0].href
        existingWorkoutData.privacy = privacyLink
      }
      delete existingWorkoutData._links
    }

    // always preserve existing aggregates when editing
    const aggregates = isEditing ? { ...copy.aggregates } : {}

    const _data = {
      ...existingWorkoutData,
      name: name,
      notes: notes,
      start_datetime: helpers.calcStartDateTime(startDate, startTime),
      start_locale_timezone: startTimeZone,
      aggregates: aggregates,
      sharing: {
        twitter: shareOnTwitter,
        facebook: shareOnFacebook,
      },
    }

    if (activityType) {
      _data.activity_type = activityType._links.self[0].href
    }

    if (gear) {
      _data.usergear = gear
    }

    if (route) {
      _data.route = route._links.self[0].href.replace(/\?.*$/, '')
    } else {
      _data.route = undefined
    }

    if (distance) {
      _data.aggregates.distance_total = helpers.calcDistance(distance, system)
    }

    if (energy) {
      _data.aggregates.metabolic_energy_total = helpers.calcEnergy(energy, system)
    }

    if (duration) {
      _data.aggregates.active_time_total = duration
      if (isCreating || (isEditing && !hasTimeSeries)) {
        _data.aggregates.elapsed_time_total = duration
      }
    } else if (isCreating) {
      _data.aggregates.active_time_total = 0
      _data.aggregates.elapsed_time_total = 0
    }

    if (steps) {
      _data.aggregates.steps_total = steps
    }

    if (heartRate) {
      _data.aggregates.heartrate_avg = heartRate
    }

    if (speed && helpers.isSpeedActivity(activityType)) {
      _data.aggregates.speed_avg = helpers.calcSpeed(speed, system, false)
    }

    if (pace && helpers.isPaceActivity(activityType)) {
      _data.aggregates.speed_avg = helpers.calcSpeed(pace, system, true)
    }

    // creating stuff
    if (isCreating) {
      _data.source = `${site || 'fitness'}_web`
      _data.reference_key = `${userId}-${uuid.v4()}-${Date.now()}-`
    }

    return _data
  }

)




// WEBPACK FOOTER //
// ./src/services/workouts/selectors.js